import { FormEvent, useEffect, useState } from 'react';
import type { Coupon, DiscountType } from '@rosita/shared';
import { DISCOUNT_TYPES } from '@rosita/shared';
import { SEO } from '../../seo/useSEO';
import { api, extractError } from '../../lib/api';
import { formatEur } from '../../lib/format';
import { useToast } from '../../components/admin/Toast';

const inputClass =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-primary focus:outline-none';
const primaryBtn =
  'rounded-md bg-admin-primary px-4 py-2 text-sm font-medium text-white hover:bg-admin-primary-hover disabled:opacity-50';
const thClass =
  'p-3 text-left text-xs font-semibold uppercase tracking-wider text-admin-muted';
const thRight = `${thClass} text-right`;

export default function AdminCouponsPage(): JSX.Element {
  const toast = useToast();
  const [items, setItems] = useState<Coupon[]>([]);
  const [code, setCode] = useState('');
  const [type, setType] = useState<DiscountType>('percentage');
  const [value, setValue] = useState(10);
  const [minOrderAmount, setMinOrderAmount] = useState(0);

  const load = async (): Promise<void> => {
    const response = await api.get<Coupon[]>('/admin/coupons');
    setItems(response.data);
  };

  useEffect(() => {
    void load();
  }, []);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      await api.post('/admin/coupons', {
        code: code.toUpperCase(),
        discountType: type,
        discountValue: value,
        minOrderAmount,
        isActive: true,
      });
      setCode('');
      setValue(10);
      setMinOrderAmount(0);
      toast.success('Cupão criado.');
      await load();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao criar cupão'));
    }
  };

  const remove = async (id: string): Promise<void> => {
    if (!window.confirm('Apagar cupão?')) return;
    await api.delete(`/admin/coupons/${id}`);
    toast.success('Cupão eliminado.');
    await load();
  };

  return (
    <>
      <SEO title="Cupões (admin)" noindex />
      <h1>Cupões</h1>
      <form onSubmit={handleSubmit} className="my-4 flex flex-wrap gap-2">
        <input
          placeholder="CÓDIGO"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          required
          className={inputClass}
        />
        <select
          value={type}
          onChange={(e) => setType(e.target.value as DiscountType)}
          className={inputClass}
        >
          {DISCOUNT_TYPES.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        <input
          type="number"
          step="0.01"
          placeholder="Valor"
          value={value}
          onChange={(e) => setValue(Number(e.target.value))}
          className={inputClass}
        />
        <input
          type="number"
          step="0.01"
          placeholder="Mínimo €"
          value={minOrderAmount}
          onChange={(e) => setMinOrderAmount(Number(e.target.value))}
          className={inputClass}
        />
        <button type="submit" className={primaryBtn}>
          Criar
        </button>
      </form>

      <table className="overflow-hidden rounded-lg bg-admin-surface shadow-sm">
        <thead>
          <tr className="border-b border-admin-border">
            <th className={thClass}>Código</th>
            <th className={thClass}>Tipo</th>
            <th className={thRight}>Valor</th>
            <th className={thRight}>Mínimo</th>
            <th className={thRight}>Usos</th>
            <th className={thClass}></th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr
              key={c.id}
              className="border-b border-admin-border last:border-b-0"
            >
              <td className="p-3 font-mono text-sm">{c.code}</td>
              <td className="p-3">{c.discountType}</td>
              <td className="p-3 text-right">
                {c.discountType === 'percentage'
                  ? `${c.discountValue}%`
                  : c.discountType === 'fixed_amount'
                    ? formatEur(c.discountValue)
                    : '—'}
              </td>
              <td className="p-3 text-right">{formatEur(c.minOrderAmount)}</td>
              <td className="p-3 text-right">
                {c.usesCount}
                {c.maxUses ? ` / ${c.maxUses}` : ''}
              </td>
              <td className="p-3 text-right">
                <button
                  type="button"
                  onClick={() => {
                    void remove(c.id);
                  }}
                  className="text-sm text-admin-danger hover:underline"
                >
                  Apagar
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
